// storage-signl.ts
// allows to create a signal to connected to storage (session or local) key
// with	the ability to set, update and get the value

import {signal, Signal, WritableSignal} from '@angular/core';

export type StorageSignal<T> = WritableSignal<T> & {
	asReadonly(): Signal<T>;
	set(value: T): void;
	update(updateFn: (prior: T) => T): void;
};

export function storageSignal<T>(
	key: string,
	initialValue: T,
	options: {
		storage?: Storage;
		tranformToValue?: (value: string) => T;
	} = {
		storage: localStorage,
		tranformToValue: (value: string) => JSON.tryParse(value, initialValue),
	},
): StorageSignal<T> {
	const storage = options.storage || localStorage;
	const tranformToValue =
		options.tranformToValue ||
		((value: string) => {
			try {
				return JSON.parse(value);
			} catch {
				return initialValue;
			}
		});

	const value: T = tranformToValue(storage.getItem(key));
	const output$: StorageSignal<T> = signal(value);

	if (value === null) storage.setItem(key, JSON.stringify(initialValue));

	// monkey patch the set method to update the storage
	const setFn = output$.set;
	const updateFn = output$.update;

	output$.set = (x: T) => {
		setFn.call(output$, x);
		storage.setItem(key, JSON.stringify(x));
	};

	output$.update = (update: (prior: T) => T) => {
		updateFn.call(output$, update);
		storage.setItem(key, JSON.stringify(output$()));
	};

	return output$;
}
